import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Compass } from 'lucide-react';

const ICON_OPTIONS = ['Shield', 'Clock', 'Users', 'Award', 'CheckCircle2', 'Sparkles', 'Heart', 'Star', 'Zap', 'Target', 'Eye', 'Lock'];

const ApproachTab = ({ authAxios }) => {
  const [data, setData] = useState({
    subtitle: 'Our Approach',
    headline: 'What Sets Us',
    headline_accent: 'Apart',
    description: 'Our commitment to excellence defines every aspect of our property operations, from initial assessment through ongoing management.',
    quote: 'Operational excellence is not a destination - it\'s our daily practice.',
    values: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await authAxios.get('/admin/sections/approach');
        if (res.data && res.data.values?.length > 0) {
          setData(prev => ({ ...prev, ...res.data }));
        }
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [authAxios]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put('/admin/sections/approach', data);
      toast.success('Approach section updated!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addValue = () => {
    setData({ ...data, values: [...data.values, { icon: 'Shield', title: '', description: '' }] });
  };

  const removeValue = (i) => {
    setData({ ...data, values: data.values.filter((_, idx) => idx !== i) });
  };

  const updateValue = (i, field, value) => {
    const values = [...data.values];
    values[i] = { ...values[i], [field]: value };
    setData({ ...data, values });
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="approach-section-tab">
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6 flex items-center gap-2">
          <Compass className="w-5 h-5 text-[#D4AF37]" />
          Approach Section
        </h2>
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Subtitle</label>
            <input value={data.subtitle} onChange={e => setData({...data, subtitle: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Headline</label>
              <input value={data.headline} onChange={e => setData({...data, headline: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Headline Accent</label>
              <input value={data.headline_accent} onChange={e => setData({...data, headline_accent: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Description</label>
            <textarea value={data.description} onChange={e => setData({...data, description: e.target.value})} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Bottom Quote</label>
            <input value={data.quote} onChange={e => setData({...data, quote: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm italic focus:border-[#D4AF37] outline-none" />
          </div>
        </div>
      </div>

      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6">Values / Cards ({data.values.length})</h2>
        <div className="space-y-3">
          {data.values.map((val, i) => (
            <div key={i} className="p-4 bg-[#0A0A0A] border border-white/5 rounded space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-1">Title</label>
                  <input value={val.title} onChange={e => updateValue(i, 'title', e.target.value)} className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-1">Icon</label>
                  <select value={val.icon} onChange={e => updateValue(i, 'icon', e.target.value)} className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none">
                    {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-1">Description</label>
                <textarea value={val.description} onChange={e => updateValue(i, 'description', e.target.value)} rows={2} className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
              </div>
              <button onClick={() => removeValue(i)} className="flex items-center gap-1.5 text-red-400/60 hover:text-red-400 font-mono text-xs">
                <Trash2 className="w-3.5 h-3.5" />Remove
              </button>
            </div>
          ))}
          <button onClick={addValue} data-testid="add-approach-value" className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors font-mono text-xs w-full justify-center">
            <Plus className="w-4 h-4" />Add Value Card
          </button>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} data-testid="save-approach-section" className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50">
        <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Approach'}
      </button>
    </div>
  );
};

export default ApproachTab;
